import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { GoogleButton } from "../auth/GoogleButton.jsx";
import { Splash } from "../components/Splash.jsx";

/* The door, and the one way through it.
 *
 * Google sign-in is the only method: there is no password to guess, reset or
 * leave behind on somebody's old laptop, and whether an address belongs to
 * the company is Google's answer rather than ours. Which addresses are
 * allowed is the access list — a domain such as @insomniacs.in, or one named
 * address — so a refusal is worded as "this address is not allowed" rather
 * than as a failure: for an address that was never going to be let in, that
 * is the truth, and there is nothing to retry. */
export function LoginPage() {
  const { user, methods, loading, signInWithGoogle, reachable } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  if (loading) return <Splash label="Checking your access" />;

  if (user) {
    const back = location.state?.from?.pathname || "/";
    return <Navigate to={back} replace />;
  }

  async function onGoogle(credential) {
    setProblem(null);
    setBusy(true);
    try {
      await signInWithGoogle(credential);
      navigate(location.state?.from?.pathname || "/", { replace: true });
    } catch (err) {
      setProblem(describe(err));
    } finally {
      setBusy(false);
    }
  }

  const domains = methods?.allowedDomains || [];

  return (
    <div className="login">
      <div className="loginbox">
        <div className="mark">
          <h1>War Room</h1>
          <span className="sub">INSOMNIACS &middot; 10 PLAYGROUNDS</span>
        </div>

        <p className="logintag">
          This board is worked by a named few. Sign in with the Google account
          on your work address.
        </p>

        {!reachable && (
          <p className="errline" role="alert">
            The War Room server is not answering. Nothing can be signed in to
            until it is back.
          </p>
        )}

        {problem && (
          <p className="errline" role="alert">
            {problem}
          </p>
        )}

        {methods?.google ? (
          <div className="loginmethod">
            <GoogleButton
              clientId={methods.googleClientId}
              onCredential={onGoogle}
              onError={setProblem}
              disabled={busy}
            />
            <p className="hintline">
              {domains.length > 0
                ? "Google accounts on " +
                  domains.map((d) => "@" + d).join(", ") +
                  " — or any address an administrator has added by name."
                : "Only addresses on the War Room access list can sign in."}
            </p>
          </div>
        ) : (
          <p className="errline">
            Google sign-in is not configured on this server, and it is the only
            way in. Set GOOGLE_CLIENT_ID on the API.
          </p>
        )}

        <p className="hintline">
          No access yet? Ask an administrator to allow your address — accounts
          are not self-served.
        </p>
      </div>
    </div>
  );
}

/* A refusal should tell the person what to do next, which is different for
   each reason the server can give. */
function describe(err) {
  switch (err.code) {
    case "not_allowed":
      return "This address is not allowed to use the War Room. Ask an administrator to add it.";
    case "google_account_mismatch":
      return "That address is registered to a different Google account.";
    case "email_unverified":
      return "That Google account's email address is not verified, so it cannot be matched to the access list.";
    case "no_email":
      return "That Google account exposes no email address, so there is nothing to match against the access list.";
    case "bad_google_token":
      return "That Google sign-in could not be verified. Try again.";
    case "too_many_attempts":
      return "Too many sign-in attempts. Wait a few minutes and try again.";
    case "google_not_configured":
      return "Google sign-in is not configured on this server.";
    case "offline":
      return "Cannot reach the War Room server.";
    default:
      return err.message || "That sign-in did not work.";
  }
}
